import { useState } from 'react';
import type { ArchivedDraft } from '../types';
import { TEAMS_BY_ABBR } from '../constants/teams';
import { TeamLogo } from './TeamLogo';

interface Props {
  archives: ArchivedDraft[];
  loading: boolean;
  currentYear: number;
  onOpenRecap2025?: () => void;
}

export function ArchiveHistory({ archives, loading, currentYear, onOpenRecap2025 }: Props) {
  const [openYear, setOpenYear] = useState<number | null>(archives[0]?.year ?? null);

  return (
    <div className="panel p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-extrabold text-white">
          📚 Past Seasons <span className="text-slate-400 text-sm font-normal">({archives.length})</span>
        </h2>
        {onOpenRecap2025 && (
          <button
            onClick={onOpenRecap2025}
            className="text-xs px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 font-bold flex items-center gap-1.5 transition"
          >
            <span>🏆</span>
            <span>2025 Season Recap & Champion</span>
          </button>
        )}
      </div>

      {loading && <p className="text-slate-500 text-sm italic">Loading archives…</p>}
      {!loading && archives.length === 0 && (
        <p className="text-slate-500 text-sm italic">
          No archived drafts yet. After this {currentYear} draft is complete, the Commissioner can archive it.
        </p>
      )}

      <div className="space-y-2">
        {archives.map(arc => {
          const isOpen = openYear === arc.year;
          return (
            <div key={arc.year} className="border border-slate-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenYear(isOpen ? null : arc.year)}
                className="w-full p-3 flex justify-between items-center bg-slate-800/60 hover:bg-slate-800 transition"
              >
                <div className="text-left">
                  <div className="text-lg font-extrabold text-teal-300">{arc.year} Season</div>
                  <div className="text-xs text-slate-400">
                    {arc.finalManagers?.length ?? 0} managers · {arc.history?.length ?? 0} picks · {arc.trades?.length ?? 0} trades
                  </div>
                </div>
                <span className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
              </button>

              {isOpen && (
                <div className="p-3 bg-slate-900/40 space-y-4">
                  {arc.year === 2025 && onOpenRecap2025 && (
                    <div className="p-3 rounded-lg bg-amber-950/30 border border-amber-500/40 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs font-black text-amber-300">🏆 2025 Champion: David (71 Wins)</div>
                        <div className="text-[11px] text-slate-300">Rob Runner-Up with 69 Wins (+2 margin). View complete breakdown.</div>
                      </div>
                      <button
                        onClick={onOpenRecap2025}
                        className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs shrink-0"
                      >
                        Full Recap
                      </button>
                    </div>
                  )}

                  {/* Final rosters */}
                  <div>
                    <h4 className="text-xs uppercase text-slate-400 font-bold mb-2">Final Rosters</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {arc.finalManagers?.map(m => (
                        <div key={m.id} className="bg-slate-800/70 rounded p-2 border border-slate-700">
                          <div className="flex justify-between items-center mb-1">
                            <div className="text-sm font-bold text-white">{m.emoji} {m.name}</div>
                            <div className="text-xs text-slate-400">
                              spent ${m.initialBudget - m.budget} / ${m.initialBudget}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {m.teams.map(abbr => {
                              const price = arc.history.find(h => h.team === abbr && h.winnerId === m.id)?.price;
                              return (
                                <div
                                  key={abbr}
                                  className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-900 rounded border border-slate-700"
                                  title={`${TEAMS_BY_ABBR[abbr]?.city} ${TEAMS_BY_ABBR[abbr]?.name}`}
                                >
                                  <TeamLogo abbr={abbr} size={14} />
                                  <span className="text-[10px] font-bold text-slate-300">{abbr}</span>
                                  {price != null && <span className="text-[10px] text-emerald-400">${price}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trades */}
                  {arc.trades && arc.trades.length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase text-slate-400 font-bold mb-2">Trades</h4>
                      <ul className="space-y-1">
                        {arc.trades.map(t => (
                          <li key={t.id} className="text-xs bg-slate-800/60 rounded p-2 border-l-2 border-teal-500">
                            <span className="font-bold text-teal-300">{t.managerAName}</span>
                            {' '}sent{' '}
                            <span className="text-amber-300">{t.teamsFromA.join(', ') || '—'}</span>
                            {t.cashFromA ? <span className="text-emerald-400"> + ${t.cashFromA}</span> : null}
                            {' '}↔{' '}
                            <span className="text-amber-300">{t.teamsFromB.join(', ') || '—'}</span>
                            {t.cashFromB ? <span className="text-emerald-400"> + ${t.cashFromB}</span> : null}
                            {' '}from{' '}
                            <span className="font-bold text-teal-300">{t.managerBName}</span>
                            {t.note && <div className="text-slate-500 italic mt-0.5">"{t.note}"</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Auction history */}
                  <details>
                    <summary className="cursor-pointer text-xs uppercase text-slate-400 font-bold">
                      Auction picks ({arc.history.length})
                    </summary>
                    <ul className="mt-2 space-y-1 max-h-80 overflow-y-auto">
                      {arc.history.map(h => (
                        <li key={`${h.round}-${h.team}`} className="flex items-center gap-2 text-xs bg-slate-800/40 rounded p-1.5">
                          <span className="font-mono text-slate-500 w-6">#{h.round}</span>
                          <TeamLogo abbr={h.team} size={18} />
                          <span className="flex-1 text-slate-300">{h.team} → {h.winner}</span>
                          <span className="text-emerald-400 font-bold">${h.price}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
